import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { flushSync } from 'react-dom'
import dayjs from 'dayjs'
import { useMenuOnSession } from '@/components/layout/MainMenuOnSessionProvider'
import { useParsedQuery } from '@/hooks/useParsedQuery'
import { MenuStorage } from '@/utils/storage/MenuStorage'
import { invalidError, notice } from '@/components/pbo/notice'
import { guardFetching } from '@/hooks/useFetchAllRows'
import { DOWNLOAD_REASON_EMPTY_MESSAGE } from '@/utils/consts'
import useExcelDownload from '@/hooks/useDownloadExcel'
import { useGetExportCombo } from '@/services/excel/useExcelQuries'
import { RETURN_RECEIPT_ITEMS_EXCEL_URL, RETURN_RECEIPT_SUMMARY_EXCEL_URL } from '@/services/return/returnReceipt/apis'
import {
  useGetReturnReceiptItems,
  useGetReturnReceiptSummaries,
} from '@/services/return/returnReceipt/useReturnReceiptQueries'
import {
  RECEIPT_TABS,
  ReturnReceiptExcelReason,
  ReturnReceiptFilter as ReturnReceiptFilterValues,
  ReturnReceiptItem,
  ReturnReceiptItemExcelScope,
  ReturnReceiptSummary,
  ReturnReceiptSummaryExcelScope,
} from '@/services/return/returnReceipt/types'
import { EXCEL_FILE_NAME_PREFIX, MESSAGES, REASON_DETAIL_MIN_LENGTH } from '@/pages/return/returnReceiptInquiry/constants'
import { isValidDownloadPassword, MESSAGES as RGR02_MESSAGES } from '@/pages/return/rgr02V2/rules'
import { fromSearchQuery, type Rgr12SearchQuery, toSearchQuery } from '@/pages/return/returnReceiptInquiry/searchQuery'
import { coreTableLayoutSlot } from '@/hooks/useCoreTableLayout'
import ReturnReceiptInquiryPageView, { type ActionCtx, type Option, type Row, type Wiring } from './handoff/page.generated'
import { usePermissionSlot } from '@/hooks/useMenuPermission'

/** 조회 실패(HTTP 400) 응답에서 서버 메시지를 꺼낸다. */
// 그리드 코드 — 개인 컬럼 배치 키와 화면관리 그리드 설정이 같은 값을 쓴다(docs/conventions/grid-code.md)
//  상세표는 배치 키가 RGR12_ITEM 하나, 설정은 탭마다 RGR12_ITEM1~5 라 값이 다르다
const HEAD_GRID_CD = 'RGR12_HEAD'

const extractSearchErrorMessage = (error: unknown): string =>
  (error as { response?: { data?: { meta?: { message?: string } } } })?.response?.data?.meta?.message || MESSAGES.searchFailed

const text = (v: unknown): string => (typeof v === 'string' ? v : '')
const asList = (v: unknown): string[] => (Array.isArray(v) ? v.map((x) => String(x ?? '').trim()).filter(Boolean) : [])
/** `FilterDate` 가 주는 ISO(`YYYY-MM-DD`) 를 필터 계약 형식(`yyyyMMdd`) 으로 — ISO 그대로 넘기면 BE 가 `CLS_DATE`(varchar(8)) 와 문자열 비교해 결과만 조용히 빠진다. */
const toClsDate = (isoDate: string): string => isoDate.replace(/-/g, '')
const today = (): string => dayjs().format('YYYY-MM-DD')
/**
 * 조회영역 값(생성본 키: ARRIVE_DATE = [from, to] ISO, rgr09Invoice = 다건 배열) → BE 조건(옛 필터 컴포넌트와 같은 변환).
 * 날짜가 비어 오면 뷰가 보여 주는 기본값(오늘)으로 본다 — 저장도 이 날짜로 한다(arriveDate).
 */
const toQueryFilter = (filters: Record<string, unknown>): { filter: ReturnReceiptFilterValues; arriveDate: [string, string] } => {
  const range = Array.isArray(filters.ARRIVE_DATE) ? (filters.ARRIVE_DATE as unknown[]) : []
  const from = text(range[0]) || today()
  const to = text(range[1]) || from
  return { filter: { clsDateFrom: toClsDate(from), clsDateTo: toClsDate(to), rtnInvoiceNos: asList(filters.rgr09Invoice) }, arriveDate: [from, to] }
}
/**
 * BE 날짜(압축형 `yyyyMMdd`·스캔일 `yyyyMMddHHmmss`)를 뷰가 읽는 ISO(`YYYY-MM-DD`)로 — 생성본 날짜 컬럼(variant dateDow)은 정확히 ISO 일 때만
 * 요일을 붙이고 아니면 원값을 그대로 그린다. 옛 columnDefinitions 의 dateDow 가 dayjs 로 풀어 주던 것을 여기서 한다(#822 리뷰 회귀 지적).
 * dev 키(assignDate 등)는 원문 그대로 둔다 — 조인 키(toPickKey)·상세 재조회·엑셀 요청 키(BE 가 원문과 '=' 비교)가 그것을 읽는다. 못 읽는 값은 원값.
 */
const toIsoDate = (v: string): string => {
  const d = dayjs(v)
  return v && d.isValid() ? d.format('YYYY-MM-DD') : v
}

/**
 * 마스터(요약) 행의 선택 키 — 뷰는 데모 축(ARRIVE_DATE·CLS_DATE·WH_NM)으로 거르지만 BE 머리표 집계축은
 * (마감일자·입고센터·**지정일자**) 3개뿐이라 그 값을 `__pick` 에 싣는다(생성본은 `__pick` 이 있으면 그것을 키로 쓴다 — 컨덕터 codegen 2026-09-28).
 * 상세는 행을 고를 때 그 행 조건으로 재조회하고(toItemFilter), 받은 상세 행에는 고른 요약 행의 이 키를 그대로 싣는다 —
 * 빈 지정일자·입고센터 요약 행은 BE 가 그 조건을 걸지 않아 상세 행의 값과 키가 갈리기 때문이다.
 */
export const toPickKey = (row: Pick<ReturnReceiptSummary, 'closeDate' | 'inCenter' | 'assignDate'>): string =>
  [row.assignDate, row.closeDate, row.inCenter].join('\u0001') // 구분자는 상세 행 정체·생성본 복합 키와 같은 제어문자(U+0001)

/**
 * 요약 행 → 뷰 행. 생성본 컬럼 키는 1.0 화면관리 컬럼 코드(CLS_DATE·TOTAL_COUNT…)다 — 화면관리 그리드 설정이 이 키로 붙는다(LOGISPRD-17094). 변환 없이 넘기면 표가 통째로 빈다. 원본(dev 키)도 남겨
 * 상세 엑셀 요청(onPick 으로 받은 이 행의 closeDate·inCenter·assignDate)이 그대로 읽는다. 첫 컬럼 센터도착일자 = 지정일자(assignDate) — BE 집계에 별도 도착일자가 없다(옛 columnDefinitions 와 같다).
 */
const toHeadViewRow = (r: ReturnReceiptSummary): Row => ({
  ...r,
  key: toPickKey(r),
  __pick: toPickKey(r),
  ARRIVE_DATE: toIsoDate(r.assignDate),
  CLS_DATE: toIsoDate(r.closeDate),
  WH_NM: r.inCenter,
  TOTAL_COUNT: r.totalQty,
  SUCCESS_COUNT: r.normalQty,
  MISS_COUNT: r.missQty,
  FAIL_COUNT: r.unregQty,
  DUP_COUNT: r.dupQty,
  ASSIGN_DATE: toIsoDate(r.assignDate),
})

/**
 * 상세 행의 정체 — BE 응답에 행 id 가 없고 반송번호는 유일하지 않다(같은 송장을 두 번 스캔한 '중복' 행·날짜가 다른 재도착). 값 전부로 정체를
 * 만들고, 값까지 똑같은 행(같은 날 같은 송장 중복 스캔)만 등장 순번(#n)으로 가른다 — 배열 위치를 키에 넣으면 재조회로 순서가 바뀔 때
 * 같은 행이 다른 키를 받아 표가 행을 갈아 끼운다(#822 봇 리뷰).
 */
const toItemIdentity = (r: ReturnReceiptItem): string =>
  // 구분자는 값에 절대 안 들어가는 제어문자(U+0001) — 생성본이 복합 키를 잇는 것과 같은 규약. '|' 는 송장·주문번호 형식이 바뀌면 충돌할 수 있다(봇 리뷰).
  [r.returnInvoiceNo, r.arriveDate, r.resultTab, r.inCenter, r.closeDate, r.assignDate, r.orgInvoiceNo, r.orderNo].join('\u0001')
const toItemRows = (items: ReturnReceiptItem[], pick: string): Row[] => {
  const seen = new Map<string, number>()
  return items.map((r) => {
    const identity = toItemIdentity(r)
    const nth = seen.get(identity) ?? 0
    seen.set(identity, nth + 1)
    return toItemViewRow(r, nth ? `${identity}#${nth}` : identity, pick)
  })
}

/** 상세 행 → 뷰 행. 키는 1.0 컬럼 코드(RGR09_ITEM*)다. SCAN_STATUS 는 탭 키(success·miss·unreg·dup)다 — 생성본 태그 컬럼의 statusMap 키이자 상태 탭이 거르는 값. 센터도착일자는 실제 스캔일(scanDate). */
const toItemViewRow = (r: ReturnReceiptItem, key: string, pick: string): Row => ({
  ...r,
  key,
  __pick: pick,
  SPR_NM: r.shipper,
  ASSIGN_DATE: toIsoDate(r.assignDate),
  CLS_DATE: toIsoDate(r.closeDate),
  SCAN_DATE: toIsoDate(r.arriveDate),
  RTN_INVOICE_NO: r.returnInvoiceNo,
  WH_NM: r.inCenter,
  SCAN_STATUS: r.resultTab,
  GI_WH_NM: r.outCenter,
  TRS_INVOICE_NO: r.orgInvoiceNo,
  ORD_NO: r.orderNo,
  INS_BY: r.worker,
})

/**
 * 고른 요약 행 → 상세 조회 조건(1.0 RGR09 더블클릭 재조회: 마감일자·입고센터·지정일자 + 조회영역 반송번호). 마감일자 단건 파라미터가 없어
 * clsDateFrom = clsDateTo 로 보낸다(BE #4603). 빈 값은 apis 가 싣지 않아 BE 가 그 조건을 걸지 않는다(1.0·상세 엑셀과 같다).
 * 화주(shipper)는 싣지 않는다 — 1.0 도 싣지 않았고, 실으면 요약과 상세 건수가 갈린다.
 */
const toItemFilter = (head: Row, rtnInvoiceNos: string[]): ReturnReceiptFilterValues => ({
  clsDateFrom: text(head.closeDate),
  clsDateTo: text(head.closeDate),
  centerNm: text(head.inCenter),
  assignDate: text(head.assignDate),
  rtnInvoiceNos,
})

/**
 * RGR09 센터도착 수령내역 조회 — 1.0 RGR09 를 신규 디자인(pbo-core)으로 재구현한 화면. (LOGISPRD-11236, dev 라우트 rgr12)
 *
 * 겉(조회영역·상하 요약/상세 표·상태 탭·표별 엑셀 사유 모달)은 컨덕터 뷰(`./handoff/page.generated`, 재핸드오프 때 통째로 갱신)가 그리고,
 * 이 파일은 옛 훅·API·검증을 그대로 두고 그 뷰에 배선(Wiring)으로 넘긴다(컴포넌트 모드, 컨덕터 문서 51).
 * 마스터 선택·상태 탭·목록 검색·페이저·모달 열림은 뷰가 가진다 — 머리표 행을 고르면 배선이 그 행 조건으로 상세를 조회하고(onPick), 탭이 그 결과를 거른다.
 *
 * 데모 대비 의도적 이탈(근거는 각 지점 주석):
 * - 사유코드 옵션은 공용 콤보(`DOWNLOAD_CD`)다. 데모의 고정 4종은 화면 설계용 값이다.
 * - 엑셀은 BE 가 암호 zip 으로 만든다(1.0 ToolBar 의 'RGR' 비밀번호 강제와 같다, LOGISPRD-14547). 사유·감사로그·행수 상한(10만)도 BE 가 진다.
 * - 생성본을 직접 고쳤다(LOGISPRD-14547) — 두 엑셀 모달의 '파일 비밀번호' 칸(RGR02 생성본과 같은 요소)·열 때 비우는 필드에 password·`onTab` 통지(상세 엑셀 tabIdx).
 *   컨덕터 데모에 아직 없다 — 재핸드오프로 덮이면 비밀번호 칸이 사라져 다운로드가 전부 막히고, onTab 이 빠지면 탭과 무관하게 전체(tabIdx 0)가 나간다. seam.test 가 둘 다 잡는다.
 * - 상세 표 마지막 컬럼(INS_BY) 제목 = 작업자(PD 확정 2026-09-28, 값 insBy) — 데모도 컨덕터 #627 로 같은 제목이 됐다(이탈 아님, 기록용).
 *
 * 옛 화면과 달라진 뷰 동작(생성본·데모 규약, 배선에서 못 바꾼다):
 * - 상세 표 '전체 N' 은 상세 전건 수다(데모 TableStat 규약 — PD 결정 2026-09-28: 다른 화면과 같은 공통 규약이라 유지). 옛 화면은 걸러진 행 수였다.
 * - 목록 검색은 컬럼 원시값만 본다 — 결과 컬럼은 탭 키(miss…)라 '오입고' 로는 안 걸린다(옛 화면은 서버 원문 result 도 검색했다). 컨덕터 후속.
 * - [조회]·[초기화]는 옛 화면처럼 머리표 선택(pick)을 풀고 상태 탭을 '전체'로 되돌린다 — 컨덕터 #637(PD 결정 2026-09-28, 마스터-디테일 공통). 뷰가 `onPick([])` 도 함께 부른다.
 */
function ReturnReceiptInquiry() {
  // 조회조건 세션 유지 — [조회] 때 메뉴 세션·URL 에 남기고(reload 로 탭바 갱신) 마운트 때 URL 에서 한 번만 읽는다(rgr01V2 방식)
  const menuSession = useMenuOnSession()
  const { parsedQs, replaceQuery } = useParsedQuery<Record<string, unknown>>()
  const [restored] = useState(() => fromSearchQuery(parsedQs))
  const persistSearch = (query: Rgr12SearchQuery) => {
    MenuStorage.menuOnSession.setSearch(query)
    menuSession.reload()
    replaceQuery(query)
  }

  // 남겨 둔 조건으로 들어오면 첫 렌더부터 그 조건을 건다 — 아래 진입 effect 의 refetch 가 이 키로 조회한다(훅은 enabled: false)
  const [queryFilter, setQueryFilter] = useState<ReturnReceiptFilterValues | null>(() => (restored ? toQueryFilter(restored).filter : null))

  const { items: summaries, refetch: refetchSummaries, isFetching: isHeadFetching } = useGetReturnReceiptSummaries(queryFilter)
  // 상세는 [조회] 때 받지 않는다 — 요약 행을 고를 때 그 행 조건으로 조회한다(1.0 RGR09 더블클릭, LOGISPRD-14913). 고른 행이 없으면 null(빈 표)
  const [picked, setPicked] = useState<{ filter: ReturnReceiptFilterValues; pick: string } | null>(null)
  const { items: pickedItems, refetch: refetchItems, isFetching: isItemFetching } = useGetReturnReceiptItems(picked?.filter ?? null)

  /** 응답은 훅이 표에 싣는다. 실패하면 서버 문구로 알린다(옛 handleSearch) */
  const fetchAndNotify = async (refetch: () => Promise<{ error: unknown }>) => {
    const { error } = await refetch()
    if (error) notice.failure(extractSearchErrorMessage(error))
  }

  // 진입 1회 — 남겨 둔 조건이 있을 때만 조회한다(없으면 옛 화면처럼 조회하지 않는다).
  //  deps 를 비워 둔 건 의도다 — restored 는 useState 초기값이라 바뀌지 않고, 마운트 뒤 다시 돌면 [조회] 없이 재조회된다
  useEffect(() => {
    if (restored) fetchAndNotify(refetchSummaries).catch(() => undefined)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // 사유코드 — 1.0·ST10 과 같이 공용 콤보(DOWNLOAD_CD). 코드/명 배열이 짝을 이루고 명이 비면 코드로 대체한다.
  const { data: combo } = useGetExportCombo({ errorToast: true })
  const reasonOptions = useMemo<Option[]>(
    () =>
      (combo?.DOWNLOAD_CD?.COMBO_OPT_CD ?? []).map((code, idx) => ({
        value: code,
        label: combo?.DOWNLOAD_CD?.COMBO_OPT_NM?.[idx] || code,
      })),
    [combo],
  )

  const headRows = useMemo(() => summaries.map(toHeadViewRow), [summaries])
  const itemRows = useMemo(() => (picked ? toItemRows(pickedItems, picked.pick) : []), [picked, pickedItems])

  /**
   * 엑셀은 BE 가 비밀번호 걸린 zip 으로 만들고 감사로그(`ExportExcelFunc`, `<사유코드>_<상세사유>`)도 BE 가 남긴다 — FE 는 요청만 보낸다(RGR02 선례).
   * 생성본 모달은 [확인] 을 잠그지 않아 사유·비밀번호를 여기서 막는다(throw = 모달 유지). 비밀번호 규칙은 BE `PasswordValidator` 미러(RGR02 rules).
   * 실패 안내는 useExcelDownload 가 띄우고 예외를 삼킨다 — 성공·실패를 못 가르므로 false(토스트 없이 닫기)를 돌려준다.
   * 진행 중 두 번째 [확인] 은 조용히 실패(throw)해 요청이 겹치지 않게 한다. 진행 플래그는 두 모달이 공유한다(동시에 열리지 않는다).
   */
  const { onDownloadExcel } = useExcelDownload<(ReturnReceiptSummaryExcelScope | ReturnReceiptItemExcelScope) & ReturnReceiptExcelReason>({ fileNamePrefix: EXCEL_FILE_NAME_PREFIX, config: { method: 'post' } })
  const downloadInFlight = useRef(false)
  const downloadWithReason = useCallback(
    async (ctx: ActionCtx, url: string, body: ReturnReceiptSummaryExcelScope | ReturnReceiptItemExcelScope | null): Promise<false> => {
      const reasonCode = text(ctx.form?.reason)
      const detailedReason = text(ctx.form?.detail).trim()
      const password = text(ctx.form?.password)
      if (!reasonCode || detailedReason.length < REASON_DETAIL_MIN_LENGTH) throw invalidError(MESSAGES.reasonInvalid)
      if (!isValidDownloadPassword(password)) throw invalidError(RGR02_MESSAGES.passwordInvalid)
      // 열기 가드가 0건을 막지만, 조회 조건·고른 요약 행이 없으면 BE 가 키 없이(범위 전체로) 뽑는다 — 보내지 않는다.
      if (!body) throw invalidError(MESSAGES.excelNoRows)
      if (downloadInFlight.current) throw new Error('download in flight')
      downloadInFlight.current = true
      try {
        await onDownloadExcel(url, { ...body, reasonCode, detailedReason, password })
        return false
      } finally {
        downloadInFlight.current = false
      }
    },
    [onDownloadExcel],
  )

  /**
   * 상세 엑셀 범위 = 고른 요약 행의 키 + 상태 탭 + 조회영역 반송번호(1.0 RGR09_ITEM 재조회 조건, BE #4503). 목록 검색은 반영하지 않는다(1.0 도 로드된 행 전부).
   * 고른 행·탭은 뷰가 가진다 — onPick·onTab 으로 받아 둔다. [조회]·[초기화] 때 뷰가 onPick([])·탭 'all' 로 되돌린다.
   */
  const pickedHead = useRef<Row | null>(null)
  // 렌더에 반영한다 — 상세표 그리드 설정이 탭마다 다르다(RGR12_ITEM1~5)
  const [activeTab, setActiveTab] = useState('all')
  const tabIdx = Math.max(0, RECEIPT_TABS.findIndex((t) => t.key === activeTab))
  const itemExcelBody = (): ReturnReceiptItemExcelScope | null => {
    const head = pickedHead.current
    if (!queryFilter || !head) return null
    const key = (v: unknown) => text(v) || null // 빈 키(NULL 요약 행)는 null — BE 가 그 조건을 걸지 않는다(1.0 동일)
    return {
      tabIdx,
      clsDate: key(head.closeDate),
      centerNm: key(head.inCenter),
      assignDate: key(head.assignDate),
      rtnInvoiceNo: queryFilter.rtnInvoiceNos,
    }
  }

  /** 0건이면 열지 않는다(행수 상한은 BE 가 400 + 문구로 막는다). 사유코드 콤보가 비면 고를 수 없으니 이유를 알린다. */
  const canDownload = useCallback(
    (count: number): boolean => {
      if (count === 0) {
        notice.invalid(MESSAGES.excelNoRows)
        return false
      }
      if (!reasonOptions.length) {
        notice.failure(DOWNLOAD_REASON_EMPTY_MESSAGE)
        return false
      }
      return true
    },
    [reasonOptions.length],
  )

  // ── 뷰 배선(Wiring) — 옛 핸들러의 본문을 슬롯에 잇는다. 배선 객체는 매 렌더 새로 만든다(뷰가 렌더 첫머리에 props.wiring 을 모듈 홀더에 대입한다 — 문서 51 ⑪). ──
  const wiring: Wiring = {
    // 상세표는 활성 탭의 그리드 — 1.0 RGR09_ITEM1~5 도 탭 순번 + 1(RGR09.js:255)
    permission: usePermissionSlot('rgr12', { rgr09HeadTable: HEAD_GRID_CD, rgr09ItemTable: `RGR12_ITEM${tabIdx + 1}` }),
    // 개인 컬럼 배치 저장 — 메인 표만(모달 안 표는 저장하지 않는다)
    columnLayout: { rgr09HeadTable: coreTableLayoutSlot(HEAD_GRID_CD), rgr09ItemTable: coreTableLayoutSlot('RGR12_ITEM') },
    rows: {
      // 배열(클라이언트 페이징) — 전건을 받아 두고 페이지 잘라내기는 뷰가 한다(옛 표와 같다). loading 만 함께 준다(total 을 주면 서버 페이징으로 본다).
      ROWS: () => ({ rows: headRows, loading: isHeadFetching }),
      // 상세는 고른 요약 행으로 조회한 행만 넘긴다(전부 그 행의 __pick) — 상태 탭·목록 검색은 뷰가 거른다. 선택 전에는 뷰가 빈 표 + 안내를 그린다.
      ROWS2: () => ({ rows: itemRows, loading: isItemFetching }),
    },
    actions: {
      /** [조회] — 조회영역 값을 BE 조건으로 바꿔(toQueryFilter) 요약만 조회하고, 그 조건을 세션·URL 에 남긴다. 상세는 행을 고를 때(onPick) 조회한다. */
      search: async (filters) => {
        const { filter, arriveDate } = toQueryFilter(filters)
        // queryFilter 커밋 전에 refetch 하면 이전 조건으로 조회된다 — flushSync 로 먼저 커밋한다(첫 await 전이라 클릭 핸들러 안에서 돈다).
        flushSync(() => setQueryFilter(filter))
        persistSearch(toSearchQuery({ ...filters, ARRIVE_DATE: arriveDate }))
        await fetchAndNotify(refetchSummaries)
      },
      /** 요약표 엑셀 — 마지막 [조회] 조건 그대로(요약표에는 목록 검색이 없다). */
      rgr09HeadDownloadModal_ok: (ctx) =>
        downloadWithReason(
          ctx,
          RETURN_RECEIPT_SUMMARY_EXCEL_URL,
          queryFilter && { clsDateFrom: queryFilter.clsDateFrom, clsDateTo: queryFilter.clsDateTo, rtnInvoiceNo: queryFilter.rtnInvoiceNos },
        ),
      /** 상세표 엑셀 — 고른 요약 행·상태 탭 기준(itemExcelBody). */
      rgr09ItemDownloadModal_ok: (ctx) => downloadWithReason(ctx, RETURN_RECEIPT_ITEMS_EXCEL_URL, itemExcelBody()),
    },
    options: {
      REASONRADIO_OPTIONS: () => reasonOptions,
      REASONRADIO_OPTIONS2: () => reasonOptions,
    },
    // 조회 기본값(센터도착일자 = 오늘)은 배선이 아니라 뷰의 날짜 칸(defaultPeriod="today")이 채운다 — filterDefaults 슬롯은 마운트 때 한 번만 계산돼
    //  자정을 넘긴 뒤 [초기화]하면 어제로 돌아간다(봇 리뷰). 뷰는 초기화마다 날짜 칸을 다시 마운트해 그때의 오늘을 넣고, search 는 비어 오면 오늘로 본다.
    /** 진입 초기값 — URL 에 남겨 둔 조건(센터도착일자·반송번호). [초기화] 는 이 값이 아니라 뷰 기본값(오늘)으로 돌아간다 */
    filterInitial: { rgr09FilterCard: (defaults) => (restored ? { ...defaults, ...restored } : defaults) },
    onPick: (rows) => {
      const head = rows[0] ?? null
      pickedHead.current = head
      // 선택 해제([조회]·[초기화]·같은 행 다시 클릭)는 상세를 비우기만 한다
      if (!head) {
        setPicked(null)
        return
      }
      // picked 커밋 전에 refetch 하면 이전 행 조건으로 조회된다 — flushSync 로 먼저 커밋한다([조회] 와 같은 이유)
      flushSync(() => setPicked({ filter: toItemFilter(head, queryFilter?.rtnInvoiceNos ?? []), pick: text(head.__pick) }))
      fetchAndNotify(refetchItems).catch(() => undefined)
    },
    onTab: (tab) => {
      setActiveTab(tab)
    },
    opens: {
      // 요약·상세 어느 쪽이든 받는 중이면 곧 덮일 결과라 열지 않는다(LOGISPRD-15269)
      hdExcelButton: () => guardFetching(isHeadFetching || isItemFetching) && canDownload(summaries.length),
      itExcelButton: (ctx) => guardFetching(isHeadFetching || isItemFetching) && canDownload(ctx.visible?.length ?? 0),
    },
    /** [초기화] — 조회 조건과 함께 목록도 비운다(옛 handleReset). 조회영역 값·페이저·체크 선택·검색어·머리표 pick·상태 탭은 뷰가 되돌린다(컨덕터 #637). */
    onReset: () => {
      setQueryFilter(null)
      persistSearch({})
    },
  }

  return <ReturnReceiptInquiryPageView wiring={wiring} />
}

export default ReturnReceiptInquiry
